import React, { useState, useEffect } from 'react';
import Catalog from './components/Catalog';
import RackPreview from './components/RackPreview';
import './App.css';
import Viewer from './components/Viewer';
import { orderModules } from './utils/moduleUtils';
import { addModule } from './service/addModuleService';
import { deleteModule } from './service/deleteModuleService';
import { Toaster } from 'react-hot-toast';

/**
 * Main application component.
 * Manages the list of selected modules, including addition, deletion, and removal of submodules.
 * Passes these states and handlers to child components to display the catalog, rack preview, and viewer.
 * 
 * @returns {JSX.Element} - The rendered App component.
 */
const App = () => {
  // State to track the list of selected modules
  const [selectedModules, setSelectedModules] = useState([]);
   
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === "INIT_DATA") {
        if (event.data?.payload?.analytics === true) {
          if (typeof window.clarity === 'function') {
            window.clarity('consent', true);
          }
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Tell parent we’re ready
    setTimeout(() => {
      window.parent.postMessage({ type: "IFRAME_READY" }, "*");
    }, 500);

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  /**
   * Handles the addition of a selected module.
   * Calls the `addModule` service and updates the state if the module is successfully added.
   * 
   * @param {Object} module - The module object to be added to the selected list.
   * @returns {Promise<void>} - Returns a Promise that resolves when the module is added.
   */
  const handleSelectModule = async (module) => {
    const m = await addModule(module, selectedModules);
    if (m && m.length) {
      setSelectedModules(m);
    }
  };

  /**
   * Handles the deletion of a module.
   * Calls the `deleteModule` service and updates the state by removing the module.
   * 
   * @param {Object} module - The module object to be removed from the selected list.
   * @returns {Promise<void>} - Returns a Promise that resolves when the module is deleted.
   */
  const handleDelete = async (module) => {
    setSelectedModules(await deleteModule(module, selectedModules));
  };

  /**
   * Handles the deletion of a submodule from a selected module.
   * Removes the submodule from the parent module and updates the state.
   * Reorders the remaining modules using the utility function `orderModules`.
   * 
   * @param {Object} subModule - The submodule object to be removed from the selected module.
   * @returns {Promise<void>} - Returns a Promise that resolves when the submodule is deleted and the modules are reordered.
   */
  const handleSubModuleDelete = async (subModule) => {
    selectedModules.forEach((m) => {
      m.subModules = m?.subModules?.filter((sm) => sm !== subModule);
    });
    const updatedModules = await orderModules(selectedModules);
    setSelectedModules(updatedModules);
  };

  return (
    <div className="app">
      {/** Catalog Component: Displays a list of available modules for selection */}
      <Catalog
        onSelectModule={handleSelectModule}
      />
      {/** Viewer Component: Displays the selected modules visually */}
      <Viewer selectedModules={selectedModules} />
      {/** RackPreview Component: Displays the selected modules in a rack preview format and allows deletion */}
      <div style={{ backgroundColor: '#EDF5FB', height: 'auto' }} className="rack-and-email">
        <RackPreview
          selectedModules={selectedModules}
          setSelectedModules={setSelectedModules}
          handleSelectModule={handleSelectModule}
          onDelete={handleDelete}
          onSubDelete={handleSubModuleDelete}
        />
      </div>
      <Toaster position="top-center" reverseOrder={false} />
    </div>

  );
};

export default App;
